import type {
  BulkPriceRequest, BulkPriceResponse,
  ItemGroupPrice, ItemGroupPriceSet, PartyPricingBasis,
  PriceList, PriceListCreate, PriceListItem, PriceListItemUpsert, PriceListRequest, PriceListUpdate, ResolvedPrice,
} from '@erp/shared';

import { api } from './api.js';

export type {
  BulkPriceLine, BulkPriceRequest, BulkPriceResponse,
  ItemGroupPrice, PartyPricingBasis, PriceList, PriceListItem, PriceListRequest, ResolvedPrice,
} from '@erp/shared';

export async function listPriceLists(): Promise<PriceList[]> {
  return (await api().get<{ items: PriceList[] }>('/pricing/lists')).data.items;
}

export async function getPriceList(id: string): Promise<PriceList> {
  return (await api().get<PriceList>(`/pricing/lists/${id}`)).data;
}

export async function createPriceList(input: PriceListCreate): Promise<PriceList> {
  return (await api().post<PriceList>('/pricing/lists', input)).data;
}

export async function updatePriceList(id: string, input: PriceListUpdate): Promise<PriceList> {
  return (await api().patch<PriceList>(`/pricing/lists/${id}`, input)).data;
}

export async function deactivatePriceList(id: string): Promise<void> {
  await api().delete(`/pricing/lists/${id}`);
}

export async function listPriceListItems(id: string): Promise<PriceListItem[]> {
  return (await api().get<{ items: PriceListItem[] }>(`/pricing/lists/${id}/items`)).data.items;
}

export async function upsertPriceListItem(id: string, input: PriceListItemUpsert): Promise<void> {
  await api().put(`/pricing/lists/${id}/items`, input);
}

export async function deletePriceListItem(id: string, rowId: string): Promise<void> {
  await api().delete(`/pricing/lists/${id}/items/${rowId}`);
}

// ── item-scoped group prices (the item form's "price per customer group") ─────

/** This item's qty-1 group price in every active price list (null = unset). */
export async function getItemGroupPrices(itemId: string): Promise<ItemGroupPrice[]> {
  return (await api().get<{ items: ItemGroupPrice[] }>(`/pricing/item/${itemId}`)).data.items;
}

/** Replace this item's per-group prices in one call (blank price clears a group). */
export async function setItemGroupPrices(itemId: string, prices: ItemGroupPriceSet['prices']): Promise<void> {
  await api().put(`/pricing/item/${itemId}`, { prices });
}

// ── customer-type (price list) approval requests ──────────────────────────────

/** Pending "customer type" requests from self-registered customers. */
export async function listPriceListRequests(): Promise<PriceListRequest[]> {
  return (await api().get<{ items: PriceListRequest[] }>('/parties/price-list-requests')).data.items;
}

/** Approve a request → apply the requested list as the customer's price list. */
export async function approvePriceListRequest(partyId: string): Promise<void> {
  await api().post(`/parties/${partyId}/price-list-request/approve`);
}

/** Reject a request → clear it (the customer keeps normal/base pricing). */
export async function rejectPriceListRequest(partyId: string): Promise<void> {
  await api().post(`/parties/${partyId}/price-list-request/reject`);
}

/** Resolve the price for an item + (customer | list) + quantity + currency. */
export async function resolvePrice(params: {
  itemId: string;
  /** A packaging unit (item_units.id) — resolves that unit's per-group price; omit for the base unit. */
  itemUnitId?: string;
  qty?: number;
  currency?: string;
  partyId?: string;
  priceListId?: string;
}): Promise<ResolvedPrice> {
  const q = new URLSearchParams({ itemId: params.itemId });
  if (params.itemUnitId) q.set('itemUnitId', params.itemUnitId);
  if (params.qty) q.set('qty', String(params.qty));
  if (params.currency) q.set('currency', params.currency);
  if (params.partyId) q.set('partyId', params.partyId);
  if (params.priceListId) q.set('priceListId', params.priceListId);
  return (await api().get<ResolvedPrice>(`/pricing/resolve?${q.toString()}`)).data;
}

/**
 * A customer's pricing BASIS — which group (price list) they're on and, above
 * all, the currency their prices are denominated in. Every sales surface drives
 * the currency it SHOWS and CHARGES from this; an all-null basis means the
 * customer has no group, so the tenant base currency applies.
 */
export async function getPartyPricingBasis(params: {
  partyId?: string;
  priceListId?: string;
}): Promise<PartyPricingBasis> {
  const q = new URLSearchParams();
  if (params.partyId) q.set('partyId', params.partyId);
  if (params.priceListId) q.set('priceListId', params.priceListId);
  return (await api().get<PartyPricingBasis>(`/pricing/basis?${q.toString()}`)).data;
}

/**
 * Batched twin of `resolvePrice` — price a whole document/grid for one customer
 * in a single call, and get their basis back with it. Each line keeps its OWN
 * currency: compare it to the document currency, never convert it client-side.
 */
export async function resolvePricesBulk(input: BulkPriceRequest): Promise<BulkPriceResponse> {
  return (await api().post<BulkPriceResponse>('/pricing/resolve-bulk', input)).data;
}
